import PropTypes from "prop-types"
import React, { Fragment, useContext, useEffect, useState } from "react"
import {
  useAsyncDebounce,
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table"
import {
  Button,
  Col,
  Row,
  Table,
} from "reactstrap"
import AppConstant from "../../constants/app.constants"
import { DefaultColumnFilter } from "./filters"

import LeftArrow from "assets/images/page/left.png"
import NextArrow from "assets/images/page/next.png"
import PreviousArrow from "assets/images/page/previous.png"
import RightArrow from "assets/images/page/right.png"
import "./table-container.scss"
import { SystemConfigurationContext } from "context/systemConfigContext"
import SearchHelper from "./SearchHelper"

// Define a default UI for filtering
function GlobalFilter({ totalRecords, setSearchFilter, handleClearSearch, searchHelperText, isTab }) {
  const [value, setValue] = React.useState("")
  const onChange = useAsyncDebounce(value => {
    setSearchFilter(value || undefined)
  }, 200)
  const { inputMax } = useContext(SystemConfigurationContext)

  useEffect(() => {
    if(isTab) {
      handleClearSearch()
      setValue("")
  
    }
  },[isTab])


  return (
    <React.Fragment>
      <Col md={2}>
        <div className="search-box me-xxl-2 my-0 my-xxl-0 d-inline-block">
          <div className="position-relative">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Search this table
              </span>
              <input
                onChange={e => {
                  setValue(e.target.value)
                  onChange(e.target.value)
                }}
                maxLength={inputMax}
                id="search-bar-0"
                type="text"
                className="form-control"
                placeholder={`${totalRecords} records...`}
                value={value || ""}
              />
              {searchHelperText && <SearchHelper helperText={searchHelperText} />}
            </label>
            <i className="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </Col>
      <Col sm="1" className="ms-2">
        <Button
          type="button"
          outline
          color="success"
          className="btn-rounded mb-2 me-2"
          onClick={() => {
            handleClearSearch()
            setValue("")
          }}
        >
          {" "}
          Clear
        </Button>
      </Col>
    </React.Fragment>
  )
}

const TableContainerV1 = ({
  columns,
  data,
  pagination,
  handlePageChange,
  handleSearchFilter,
  handleSortColumn,
  customSection,
  customFilterSection,
  handleClearSearch,
  sortOptions,
  customPageSize = AppConstant.TABLE_RECORD_SIZE,
  className,
  customPageSizeOptions = AppConstant.TABLE_RECORD_OPTIONS,
  hideSearchView = false,
  hidePageSize = false,
  searchHelperText = '',
  isTab = false,
  isTotal = false,
  TotalData= ''
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setPageSize,
    state: { pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageSize: customPageSize || AppConstant.TABLE_RECORD_SIZE,
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  )

  const { pageIndex = 0, pageCount = 0, totalRecords = 0 } = pagination || {}
  const [pageNumberArray, setPageNumberArray] = useState([])

  useEffect(() => {
    setPageNumberArray([])
    const minPage = Math.min(7, pageCount)
    const currentPage = pageIndex + 1

    if (pageCount <= 7) {
      for (let index = 1; index <= minPage; index++) {
        setPageNumberArray(current => [
          ...current,
          { page: index, type: "number" },
        ])
      }
    } else {
      const startValue = currentPage - 5
      const endValue = pageCount - currentPage

      let startIndex = 0
      let endIndex = 0

      if (startValue < 0) {
        endIndex = 5
      } else if (startIndex >= 0 && endValue < 4) {
        startIndex = pageCount - 5
        endIndex = pageCount
      } else if (startIndex >= 0 && endValue >= 4) {
        startIndex = currentPage - 2
        endIndex = startIndex + 3
      }

      const startDis = startIndex - 1
      if (startDis > 1) {
        setPageNumberArray(current => [...current])
        setPageNumberArray(current => [
          ...current,
        ])
      }

      for (let index = startIndex; index < endIndex; index++) {
        setPageNumberArray(current => [
          ...current,
          { page: index + 1, type: "number" },
        ])
      }

      let endDis = endIndex - 1
      if (endDis > 1 && startIndex < pageCount - 5) {
        setPageNumberArray(current => [
          ...current,
          { page: "...", type: "end" },
        ])
        setPageNumberArray(current => [
          ...current,
          { page: pageCount, type: "number" },
        ])
      }
    }
  }, [pagination])


  useEffect(() => {
    if(isTab) {
     setPageSize(10)
  
    }
  },[isTab])



  const generateSortingIndicator = (column) => {
    const isColumnSorted = sortOptions.column === column.id;
    const isColumnInOptional = sortOptions.optionalColumn.indexOf(column.id) === -1;
    const isAsc = sortOptions.isAsc;

    if (isColumnSorted && isColumnInOptional) {
      return isAsc ? "bx bx-chevron-up ps-1 pe-1 font-size-20 fw-bolder" : "bx bx-chevron-down ps-1 pe-1 font-size-20 fw-bolder";
    }

    return isColumnInOptional ? "bx bx-chevron-up ps-1 pe-1 font-size-20 grey" : "";
  };

  const onChangeInSelect = event => {
    setPageSize(Number(event.target.value))
    handlePageChange(1, Number(event.target.value))
  }

  const gotoPage = page => {
    if (page > 0 && page <= pageCount) handlePageChange(page, pageSize)
  }

  const paginationClick = (pageData, index) => {
    const { page, type } = pageData
    if (type == "number") gotoPage(page)
    else if (type == "start") {
      const nextPage = pageNumberArray[index + 1]
      gotoPage(nextPage.page - 1)
    } else {
      const nextPage = pageNumberArray[index - 1]
      gotoPage(nextPage.page + 1)
    }
  }

  const handleSortColumnClick = column => {
    if (!sortOptions || sortOptions.optionalColumn.indexOf(column.id) > -1)
      return

    if (sortOptions.column == column.id) {
      handleSortColumn(
        Object.assign(sortOptions, {
          column: column.id,
          isAsc: !sortOptions.isAsc,
        })
      )
    } else {
      handleSortColumn(
        Object.assign(sortOptions, { column: column.id, isAsc: false })
      )
    }
  }

  const callSearchFilter = value => {
    handleSearchFilter(value, pageSize)
  }

  const canNextPage = () => {
    return pageIndex < pageCount
  }

  const canPreviousPage = () => {
    return pageIndex > 1
  }
  return (
    <Fragment>
      <Row className="mb-2">

        <Col md="9">

          <div className="d-flex flex-column flex-md-row gap-1">
            {!hidePageSize &&
              <select
                className="form-select  me-3 show-dropdown"
                value={pageSize}
                onChange={onChangeInSelect}
              >
                {customPageSizeOptions.map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            }
            <div className="me-3">{customFilterSection}</div>

            {!hideSearchView && handleSearchFilter && (
              <GlobalFilter
                searchHelperText={searchHelperText}
                totalRecords={totalRecords}
                setSearchFilter={callSearchFilter}
                handleClearSearch={handleClearSearch}
                isTab={isTab}
              />
            )}
          </div>

        </Col>

        <Col sm="3">{customSection}</Col>
      </Row>

      <div className="table-responsive react-table">
        <Table bordered hover {...getTableProps()} className={className}>
          <thead className="table-light table-nowrap">
            {headerGroups.map(headerGroup => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th key={column.id}>
                    <div
                      className={`${column.alignRight ? `align_right` : 'align_left'}`}
                      onClick={() => handleSortColumnClick(column)}
                    >
                      {column.render("Header")}
                      {sortOptions && (
                        <i className={generateSortingIndicator(column)}></i>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {page.length == 0 ? (
            <tbody>
              <tr>
                <td colSpan={headerGroups[0].headers.length} className="text-center">No data found</td>
              </tr>
            </tbody>
          ) : (
            <tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row)
                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr>
                      {row.cells.map(cell => {
                        return (
                          <td
                            key={cell.id}
                            {...cell.getCellProps()}
                            className={`${index % 2 == 0
                              ? "table-row-odd"
                              : "table-row-even"
                              }`}
                          >
                            {cell.render("Cell")}
                          </td>
                        )
                      })}
                    </tr>
                  </Fragment>
                )
              })}
              {isTotal && (TotalData)}
            </tbody>
          )}
        </Table>
      </div>

      <Row>
        <Col
          md="6"
          className="d-flex justify-content-md-start justify-content-center align-items-center mt-md-0 mb-md-0 mt-2 mb-2"
        >
          {pageIndex > 0 &&
            <>
              Showing
              <span className="text-bold">{((pageIndex - 1) * pageSize + 1) || 0}</span> to
              <span className="text-bold">
                {((pageIndex - 1) * pageSize + (data.length || 0)) || 0}
              </span>{" "}
              of
              <span className="text-bold">{totalRecords || 0}</span> records
            </>
          }
        </Col>
        <Col
          md="6"
          className="d-flex justify-content-md-end justify-content-center align-items-center"
        >
          {pageIndex > 0 &&
            <>
              <div className="d-flex gap-1">
                <img
                  className={`pagination__button pagination__page-first ${!canPreviousPage() ? "pagination__button--disabled" : ""
                    }`}
                  src={LeftArrow}
                  alt="FirstPage"
                  onClick={() => gotoPage(1)}
                  title="Go to First page"
                />
                <img
                  className={`pagination__button pagination__page-previous ${!canPreviousPage() ? "pagination__button--disabled" : ""
                    }`}
                  src={PreviousArrow}
                  alt="PrevPage"
                  onClick={() => {
                    gotoPage(pageIndex - 1)
                  }}
                  title="Previous Page"
                />
              </div>
              <div className="d-flex">
                {pageNumberArray.map((page, index) => {
                  return (
                    <Fragment key={page.page}>
                      <span
                        color="primary"
                        key={page}
                        onClick={() => {
                          paginationClick(page, index)
                        }}
                        className={`pagination_number ${pageIndex == page.page
                          ? "pagination_number--selected"
                          : ""
                          }`}
                      >
                        {page.page}
                      </span>
                    </Fragment>
                  )
                })}
              </div>
              <div className="d-flex gap-1">
                <img
                  className={`pagination__button pagination__page-first ${!canNextPage() ? "pagination__button--disabled" : ""
                    }`}
                  src={NextArrow}
                  alt="GoToNext"
                  onClick={() => gotoPage(pageIndex + 1)}
                  title="Next Page"
                />
                <img
                  className={`pagination__button pagination__page-previous ${!canNextPage() ? "pagination__button--disabled" : ""
                    }`}
                  src={RightArrow}
                  alt="LasPage"
                  onClick={() => {
                    gotoPage(pageCount)
                  }}
                  title="Go to Last Page"
                />
              </div>
            </>
          }
        </Col>
      </Row>
    </Fragment >
  )
}

TableContainerV1.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
}

export default TableContainerV1
